import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { apiPost } from '../../utils/api';
import { useModal } from '../../context/ModalContext';

/**
 * Unified Add Lead / Promote to Lead modal.
 * Props:
 *   - onClose: close the modal
 *   - onCreated: callback after successful create/promote
 *   - prefillPhone: optional phone number to pre-fill (auto-captured from Unibox)
 *   - prefillName: optional name to pre-fill
 *   - contactId: if provided, promotes existing contact instead of creating new
 *   - sessionId: optional session ID to associate with the contact
 */
export default function AddLeadModal({ onClose, onCreated, prefillPhone, prefillName, contactId, sessionId }) {
  const modal = useModal();

  // Split prefill name into first/last — skip if name is just a phone number
  const rawName = (prefillName || '').trim();
  const isNameJustDigits = /^\d+$/.test(rawName.replace(/\s/g, ''));
  const nameParts = isNameJustDigits ? [] : rawName.split(/\s+/);

  const [form, setForm] = useState({
    phone: prefillPhone || '',
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
    email: '',
    stage: 'new',
    tags: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const sanitizedPhone = form.phone.replace(/\D/g, '');
    if (!contactId && (sanitizedPhone.length < 7 || sanitizedPhone.length > 15)) {
      modal.alert('Phone number must be between 7 and 15 digits.');
      return;
    }

    const tagsArray = form.tags
      ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : [];

    setSubmitting(true);
    try {
      if (contactId) {
        // Promote existing contact
        await apiPost(`/api/contacts/${contactId}/promote`, {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          stage: form.stage,
          tags: tagsArray,
          notes: form.notes,
        });
        modal.alert('Contact promoted to lead!', 'success');
      } else {
        // Create new contact as lead
        await apiPost('/api/contacts', {
          phone: sanitizedPhone,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          stage: form.stage,
          tags: tagsArray,
          notes: form.notes,
          sessionId: sessionId || '',
          isLead: true,
        });
        modal.alert('Lead created successfully.', 'success');
      }
      onCreated?.();
      onClose();
    } catch (err) {
      modal.alert(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const isPromote = !!contactId;
  const INPUT_CLS = 'w-full border border-surface-border rounded-lg px-3 py-2 text-sm outline-none focus:border-brand/40 transition-colors';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-surface-card rounded-xl shadow-2xl w-[440px] max-w-[90vw] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-border px-4 py-3">
          <h2 className="text-base font-semibold text-ink-primary">
            {isPromote ? 'Promote to Lead' : 'Add Lead'}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-4 space-y-3">
            {/* Phone Number — read-only if prefilled, editable only for brand new leads */}
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">
                Phone Number {!prefillPhone && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required={!prefillPhone}
                readOnly={!!prefillPhone}
                placeholder="919876543210"
                className={`${INPUT_CLS} ${prefillPhone ? 'bg-surface-bg text-ink-muted cursor-not-allowed' : ''}`}
              />
            </div>

            {/* First Name / Last Name */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-ink-secondary mb-1">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  className={INPUT_CLS}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-ink-secondary mb-1">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  className={INPUT_CLS}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="email@example.com"
                className={INPUT_CLS}
              />
            </div>

            {/* Stage */}
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Stage</label>
              <select name="stage" value={form.stage} onChange={handleChange} className={INPUT_CLS}>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="converted">Converted</option>
                <option value="lost">Lost</option>
              </select>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Tags</label>
              <input
                type="text"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="comma-separated, e.g. hot, enterprise"
                className={INPUT_CLS}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-1">Notes</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Add notes..."
                className={`${INPUT_CLS} resize-none`}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-surface-border hover:bg-surface-bg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary px-4 py-2 text-sm rounded-lg disabled:opacity-50"
            >
              {submitting ? 'Saving...' : isPromote ? 'Promote to Lead' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
